import type { GameProvider } from '@/features/games/data/gameProviders';

/** Synthetic filter for inactive providers (status from API). */
export const HOME_COMING_SOON_FILTER_ID = '__status:inactive__';

export const HOME_TAG_VISIBLE_MAX = 8;

export interface HomeProviderTagFilter {
  id: string;
  label: string;
  count: number;
}

function normalizeTagId(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Build Home browse chips from fetched providers — unique clientTag values only.
 * Sorted by usage count, then label.
 */
export function buildHomeProviderTagFilters(providers: GameProvider[]): HomeProviderTagFilter[] {
  const byId = new Map<string, HomeProviderTagFilter>();

  for (const provider of providers) {
    const raw = provider.clientTag?.trim();
    if (!raw) continue;
    const id = normalizeTagId(raw);
    const existing = byId.get(id);
    if (existing) {
      existing.count += 1;
    } else {
      byId.set(id, { id, label: raw, count: 1 });
    }
  }

  const tags = Array.from(byId.values()).sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );

  // No synthetic "Coming soon" chip — inactive stays in All with tile overlay (V1).
  return tags;
}

export function matchesHomeProviderTag(card: GameProvider, filterId: string): boolean {
  if (filterId === HOME_COMING_SOON_FILTER_ID) {
    return card.status === 'inactive';
  }
  const tag = card.clientTag?.trim();
  if (!tag) return false;
  return normalizeTagId(tag) === filterId;
}

export function isHomeProviderTagFilterActive(
  filterId: string,
  available: HomeProviderTagFilter[]
): boolean {
  if (filterId === 'all') return true;
  return available.some((tag) => tag.id === filterId);
}

/**
 * 0 = playable, 1 = under maintenance (the health snapshot closed deposits — the same test as
 * `isProviderDepositLocked` — or the API status says so), 2 = coming soon. A Hot tag does not change
 * the rank. Kept free of the health service so this stays a pure helper.
 */
function availabilityRank(game: { status?: string | null; depositsOpen?: boolean }): number {
  if (game.status === 'inactive') return 2;
  if (game.status === 'maintenance' || game.depositsOpen === false) return 1;
  return 0;
}

/**
 * Catalog lists (Play's browse grid, the Add money game pickers): playable games first in their
 * catalog order, then games under maintenance, then Coming soon — each group keeps its own order.
 * Returns a new array.
 */
export function sortUnavailableGamesLast<T extends { status?: string | null; depositsOpen?: boolean }>(
  games: readonly T[]
): T[] {
  return games
    .map((game, index) => ({ game, index, rank: availabilityRank(game) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ game }) => game);
}

/** How many suggested games to show in Continue when the user has no linked accounts. */
export const CONTINUE_FALLBACK_COUNT = 4;

/**
 * Pick Continue-strip fallbacks when there are no linked wallets.
 * Priority: playable before under maintenance; then tagged games first (by tag popularity), then
 * remaining active games by name.
 */
export function pickContinueFallbackProviders(
  providers: GameProvider[],
  limit = CONTINUE_FALLBACK_COUNT
): GameProvider[] {
  const active = providers.filter(
    (p) => p.status !== 'inactive' && p.status !== 'maintenance'
  );
  if (active.length === 0 || limit <= 0) return [];

  const tagCounts = new Map<string, number>();
  for (const p of active) {
    const tag = p.clientTag?.trim().toLowerCase();
    if (!tag) continue;
    tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
  }

  const score = (p: GameProvider): number => {
    const tag = p.clientTag?.trim().toLowerCase();
    if (!tag) return 0;
    return 1000 + (tagCounts.get(tag) ?? 0);
  };

  return [...active]
    .sort((a, b) => {
      const byAvailability = availabilityRank(a) - availabilityRank(b);
      if (byAvailability !== 0) return byAvailability;
      const byScore = score(b) - score(a);
      if (byScore !== 0) return byScore;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    })
    .slice(0, limit);
}
